import createCache from "@emotion/cache";
import { CacheProvider } from "@emotion/react";
import { QueryClient } from "@tanstack/react-query";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import { DataStatusProvider } from "components-redesign/DataStatusBadge/DataStatusContext";
import { SnackbarStack } from "components-redesign/Snackbar";
import ErrorBoundary from "components/ErrorBoundary/ErrorBoundary";
import RouteAgnosticDialogs from "components/RouteAgnosticDialogs";
import { FeatureTogglesProvider } from "configuration/featureToggles/FeatureTogglesContext";
import { LayoutGroup } from "framer-motion";
import { createIDBPersister } from "helpers/useQuery/idbPersister";
import React from "react";
import { Provider } from "react-redux";
import { BrowserRouter } from "react-router";
import { scan } from "react-scan";
import store from "redux/store";
import { CampaignDateRangeProvider } from "routes/Campaigns/CampaignDateRangeContext";
import { LastLocationProvider } from "routing/LastLocation";
import Routes from "routing/Routes";
import ThemeProvider from "styles/ThemeProvider";

scan({ enabled: process.env.PERFORMANCE_SCAN === "true" });

/* Query setup */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      staleTime: 10 * 1000,
      gcTime: 10 * 1000, // Refer to README for explanations
    },
  },
});

const persister = createIDBPersister();

// mui goes first in <head> so our makeStyles overrides win, like JSS did
const muiCache = createCache({ key: "mui", prepend: true });
// <Global>s like CssBaseline land before the cache's first tag, or at the end of <head> when it has none yet,
// so give it one up top or CssBaseline ends up overriding our own body/heading styles
muiCache.sheet.insert(":root{}");

class App extends React.PureComponent {
  render() {
    return (
      <PersistQueryClientProvider
        client={queryClient}
        persistOptions={{ persister }}>
        <ErrorBoundary>
          <LayoutGroup>
            <BrowserRouter>
              <LastLocationProvider>
                <ThemeProvider>
                  <CacheProvider value={muiCache}>
                    <Provider store={store}>
                      <RouteAgnosticDialogs />
                      <FeatureTogglesProvider>
                        <CampaignDateRangeProvider>
                          <SnackbarStack />
                          <DataStatusProvider>
                            <Routes />
                          </DataStatusProvider>
                        </CampaignDateRangeProvider>
                      </FeatureTogglesProvider>
                    </Provider>
                  </CacheProvider>
                </ThemeProvider>
              </LastLocationProvider>
            </BrowserRouter>
          </LayoutGroup>
        </ErrorBoundary>
      </PersistQueryClientProvider>
    );
  }
}

export default App;
